import React, { useState, useEffect } from 'react';
import axios from 'axios';
import styles from './IssueTracker.module.css';

interface IssueReport {
  id: number;
  issueType: string;
  priority: string;
  title: string;
  description: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  pageUrl?: string;
  stepsToReproduce?: string;
  expectedBehavior?: string;
  actualBehavior?: string;
  resolutionNotes?: string;
  Reporter: {
    id: number;
    username: string;
    email: string;
  };
  AssignedUser?: {
    id: number;
    username: string;
  };
  Mercenary?: {
    id: number;
    name: string;
  };
}

interface IssueStats {
  open: number;
  inProgress: number;
  resolved: number;
  closed: number;
  critical: number;
  high: number;
  lastWeek: number;
  total: number;
}

const IssueTracker: React.FC = () => {
  const [issues, setIssues] = useState<IssueReport[]>([]);
  const [stats, setStats] = useState<IssueStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedIssue, setSelectedIssue] = useState<IssueReport | null>(null);
  const [filters, setFilters] = useState({
    status: 'open',
    issueType: '',
    priority: '',
    search: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });

  const issueTypes = [
    { value: '', label: 'All Types' },
    { value: 'bug', label: 'Bug Report' },
    { value: 'game_mechanic', label: 'Game Mechanic' },
    { value: 'balance', label: 'Balance Issue' },
    { value: 'feature_request', label: 'Feature Request' },
    { value: 'ui_ux', label: 'UI/UX Issue' },
    { value: 'performance', label: 'Performance' },
    { value: 'other', label: 'Other' }
  ];

  const statuses = [
    { value: '', label: 'All Statuses' },
    { value: 'open', label: 'Open' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'resolved', label: 'Resolved' },
    { value: 'closed', label: 'Closed' },
    { value: 'duplicate', label: 'Duplicate' }
  ];

  const priorities = [
    { value: '', label: 'All Priorities' },
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'critical', label: 'Critical' }
  ];

  useEffect(() => {
    fetchIssues();
    fetchStats();
  }, [filters, pagination.page]);

  const fetchIssues = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
        ...Object.fromEntries(Object.entries(filters).filter(([_, value]) => value))
      });

      const response = await axios.get(`/api/issues/all?${params}`, {
        withCredentials: true
      });

      setIssues(response.data.reports);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination.total,
        totalPages: response.data.pagination.totalPages
      }));
    } catch (error) {
      console.error('Error fetching issues:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get('/api/issues/stats/overview', {
        withCredentials: true
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleStatusUpdate = async (issueId: number, newStatus: string, resolutionNotes?: string) => {
    try {
      await axios.patch(`/api/issues/${issueId}/status`, {
        status: newStatus,
        resolutionNotes
      }, { withCredentials: true });

      // Refresh issues and stats
      fetchIssues();
      fetchStats();
      
      // Update selected issue if it's the one being updated
      if (selectedIssue && selectedIssue.id === issueId) {
        setSelectedIssue(prev => prev ? { ...prev, status: newStatus } : null);
      }
    } catch (error) {
      console.error('Error updating issue status:', error);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return '#ff4444';
      case 'high': return '#ff8800';
      case 'medium': return '#ffaa00';
      case 'low': return '#00aa00';
      default: return '#666';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return '#ff6b6b';
      case 'in_progress': return '#4ecdc4';
      case 'resolved': return '#45b7d1';
      case 'closed': return '#96ceb4';
      case 'duplicate': return '#feca57';
      default: return '#666';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className={styles.issueTracker}>
      <div className={styles.header}>
        <h1>Issue Tracker</h1>
        
        {stats && (
          <div className={styles.statsGrid}>
            <div className={styles.statCard}>
              <div className={styles.statNumber}>{stats.total}</div>
              <div className={styles.statLabel}>Total Issues</div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statNumber}>{stats.open}</div>
              <div className={styles.statLabel}>Open</div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statNumber}>{stats.inProgress}</div>
              <div className={styles.statLabel}>In Progress</div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statNumber}>{stats.critical + stats.high}</div>
              <div className={styles.statLabel}>High Priority</div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statNumber}>{stats.lastWeek}</div>
              <div className={styles.statLabel}>This Week</div>
            </div>
          </div>
        )}
      </div>

      <div className={styles.filters}>
        <div className={styles.filterGroup}>
          <select
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
          >
            {statuses.map(status => (
              <option key={status.value} value={status.value}>
                {status.label}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.filterGroup}>
          <select
            value={filters.issueType}
            onChange={(e) => handleFilterChange('issueType', e.target.value)}
          >
            {issueTypes.map(type => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.filterGroup}>
          <select
            value={filters.priority}
            onChange={(e) => handleFilterChange('priority', e.target.value)}
          >
            {priorities.map(priority => (
              <option key={priority.value} value={priority.value}>
                {priority.label}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.filterGroup}>
          <input
            type="text"
            placeholder="Search issues..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
          />
        </div>
      </div>

      <div className={styles.content}>
        <div className={styles.issuesList}>
          {loading ? (
            <div className={styles.loading}>Loading issues...</div>
          ) : issues.length === 0 ? (
            <div className={styles.noIssues}>No issues found</div>
          ) : (
            <>
              {issues.map(issue => (
                <div
                  key={issue.id}
                  className={`${styles.issueCard} ${selectedIssue?.id === issue.id ? styles.selected : ''}`}
                  onClick={() => setSelectedIssue(issue)}
                >
                  <div className={styles.issueHeader}>
                    <div className={styles.issueTitle}>
                      <span className={styles.issueId}>#{issue.id}</span>
                      {issue.title}
                    </div>
                    <div className={styles.issueMeta}>
                      <span
                        className={styles.priority}
                        style={{ backgroundColor: getPriorityColor(issue.priority) }}
                      >
                        {issue.priority}
                      </span>
                      <span
                        className={styles.status}
                        style={{ backgroundColor: getStatusColor(issue.status) }}
                      >
                        {issue.status.replace('_', ' ')}
                      </span>
                    </div>
                  </div>
                  
                  <div className={styles.issueInfo}>
                    <div className={styles.issueType}>{issue.issueType.replace('_', ' ')}</div>
                    <div className={styles.issueReporter}>by {issue.Reporter.username}</div>
                    <div className={styles.issueDate}>{formatDate(issue.createdAt)}</div>
                  </div>
                </div>
              ))}

              <div className={styles.pagination}>
                <button
                  disabled={pagination.page === 1}
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                >
                  Previous
                </button>
                <span>
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <button
                  disabled={pagination.page === pagination.totalPages}
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                >
                  Next
                </button>
              </div>
            </>
          )}
        </div>

        {selectedIssue && (
          <div className={styles.issueDetails}>
            <div className={styles.detailsHeader}>
              <h2>Issue #{selectedIssue.id}</h2>
              <button
                className={styles.closeDetails}
                onClick={() => setSelectedIssue(null)}
              >
                ×
              </button>
            </div>

            <div className={styles.detailsContent}>
              <div className={styles.detailsSection}>
                <h3>{selectedIssue.title}</h3>
                <div className={styles.detailsMeta}>
                  <span>Type: {selectedIssue.issueType.replace('_', ' ')}</span>
                  <span>Priority: {selectedIssue.priority}</span>
                  <span>Status: {selectedIssue.status.replace('_', ' ')}</span>
                  <span>Reporter: {selectedIssue.Reporter.username}</span>
                  <span>Created: {formatDate(selectedIssue.createdAt)}</span>
                </div>
              </div>

              <div className={styles.detailsSection}>
                <h4>Description</h4>
                <p>{selectedIssue.description}</p>
              </div>

              {selectedIssue.stepsToReproduce && (
                <div className={styles.detailsSection}>
                  <h4>Steps to Reproduce</h4>
                  <pre>{selectedIssue.stepsToReproduce}</pre>
                </div>
              )}

              {selectedIssue.expectedBehavior && (
                <div className={styles.detailsSection}>
                  <h4>Expected Behavior</h4>
                  <p>{selectedIssue.expectedBehavior}</p>
                </div>
              )}

              {selectedIssue.actualBehavior && (
                <div className={styles.detailsSection}>
                  <h4>Actual Behavior</h4>
                  <p>{selectedIssue.actualBehavior}</p>
                </div>
              )}

              {selectedIssue.pageUrl && (
                <div className={styles.detailsSection}>
                  <h4>Page URL</h4>
                  <a href={selectedIssue.pageUrl} target="_blank" rel="noopener noreferrer">
                    {selectedIssue.pageUrl}
                  </a>
                </div>
              )}

              <div className={styles.detailsSection}>
                <h4>Update Status</h4>
                <div className={styles.statusUpdate}>
                  {statuses.slice(1).map(status => (
                    <button
                      key={status.value}
                      className={`${styles.statusButton} ${
                        selectedIssue.status === status.value ? styles.currentStatus : ''
                      }`}
                      onClick={() => handleStatusUpdate(selectedIssue.id, status.value)}
                      disabled={selectedIssue.status === status.value}
                    >
                      {status.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default IssueTracker;
